import ReactMarkdown from 'react-markdown';
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Typography } from 'antd';
import { DiscordLink } from '../../components/DiscordLink';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { useIsBigScreen } from '../../app/hooks'
// Code blocks? https://github.com/rajinwonderland/react-code-blocks
// const commonStyles = {padding: '0 50px 50px', maxWidth: "1200px"}
const commonStyles = {maxWidth: "1200px"}
const fancyStyles = {fontSize: '0.9rem', fontFamily: 'Noto Serif, serif', ...commonStyles}
const regularStyles = {fontSize: '1.0rem', ...commonStyles}
// const regularStyles = {fontSize: '1.0rem', padding: '0 50px', maxWidth: "1200px"}


const RouterLink = (props: any) => {
    if (props.href === 'fs_discord') {
        return <DiscordLink text={props.children}/>
    }
    if (props.href === 'fs_discord_widget') {
        return <iframe
        src="https://discord.com/widget?id=1013674564460490753&theme=dark"
        width="350" height="500"
        allowTransparency={true}
        // frameBorder="0"
        title="discord"
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts">
        </iframe>
    }
    if (props.href === 'copy') {
        return <>
            <CopyToClipboard text={props.children} content={<a>{props.children}</a>} />
        </>
    }

    return (
        props.href.match(/^(https?:)?\/\//)
          ? <a href={props.href} target='blank'>{props.children}</a>
          : <Link to={props.href}>{props.children}</Link>
      )
}

const LinkableHeader = (props: any, size: number) => {
    const id = props.children.toString().toLowerCase().replace(/[^a-z0-9]/g,"-")
    const page = `${window.location}#${id}`
    if (size === 1) {
        return <Typography.Title level={1} id={id}><CopyToClipboard text={page} placement='top' tooltip='Copy Link' content={props.children} /></Typography.Title>
    } else if (size === 2) {
        return <Typography.Title level={2} id={id}><CopyToClipboard text={page} placement='top' tooltip='Copy Link' content={props.children} /></Typography.Title>
    } else if (size === 3) {
        return <Typography.Title level={3} id={id}><CopyToClipboard text={page} placement='top' tooltip='Copy Link' content={props.children} /></Typography.Title>
    }
    return <Typography.Title level={4} id={id}><CopyToClipboard text={page} placement='top' tooltip='Copy Link' content={props.children} /></Typography.Title>
}

const LinkableH2 = (props: any) => LinkableHeader(props, 2)
const LinkableH3 = (props: any) => LinkableHeader(props, 3)

const UnlinkableH1 = (props: any) => <Typography.Title level={1}>{props.children}</Typography.Title>

const StyledImage = (props: any) => {
    const isBigScreen = useIsBigScreen()

    return (
        <img style={{maxWidth: isBigScreen ? '800px' : '100%'}} src={props.src} alt={props.alt} title={props.title} />
    )
}

const StyledPre = (props: any) => {
    return (
        <pre style={{border: '1px solid #555', padding: '10px', fontSize: '0.8rem'}}>{props.children}</pre>
    )
}

const StyledParagraph = (props: any) => {
    return (
        <Typography.Paragraph>{props.children}</Typography.Paragraph>
    )
}

const StyledCode = (props: any) => {
    return (
        <code style={{border: '1px solid #555', padding: '10px', fontSize: '0.8rem'}}>{props.children}</code>
    )
}

const getPageMarkdownContent = (markdown: string, additional_page_content_styles?: React.CSSProperties) => {
    const styles = additional_page_content_styles ? additional_page_content_styles : regularStyles

    return (
        <div className="App">
          <div className="App-content">
            <div className="App-page-content" style={styles}>
                <ReactMarkdown components={{
                    a: RouterLink, img: StyledImage,
                    h1: UnlinkableH1, h2: LinkableH2, h3: LinkableH3,
                    pre: StyledPre,
                    p: StyledParagraph}}>{markdown}</ReactMarkdown>
            </div>
          </div>
        </div>
      );
}

export function EmailBotContent() {
    const markdown = `# Email Bot
Email yourself or your community automatically or through curation. Set up your workflow to sent an immediate email or add to your Daily Digest.

Instant Emails and Daily Digest emails are currently available as a beta to some users before being generally available. If you want a sneak peek, [contact us](/contact_us).
`
    useEffect(() => {document.title = 'FreqSignals | Email Bot'}, [])
    return getPageMarkdownContent(markdown)
}

export function TwitterBotContent() {
    const markdown = `# Twitter
Notify your Twitter followers automatically or through curation of signals you're seeing.

Or automatically pull in tweets from an account to use as a signal.

The twitter integration is on hold to see if there are any major changes from Twitter's acquisition. If you have a usecase you'd like to see, [contact us](/contact_us).
    `
    useEffect(() => {document.title = 'FreqSignals | Twitter'}, [])
    return getPageMarkdownContent(markdown)
}

export function DiscordBotContent() {
    const markdown = `# Discord Bot
Join our [community](/community) on Discord!

Notify your Discord community automatically or through curation of signals you're seeing. FreqSignals leverages Discord Webhooks to send notifications to your Discord server.

You can set up a Discord Webhook integration in the [Integrations](/integrations) page and configure your notifications in [Workflows](/workflows).

Create your webhook in your Discord Community by:

1. Right Click on your Community. Go Server Settings > Integrations
2. Click View Webhooks
3. Click New Webhook
4. Copy the Webhook URL
5. Enter that Webhook URL on the [FreqSignals Integrations Page](/integrations)

Learn more about [Discord Webhooks in Discord's Documentation](https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks)

Advanced messaging is now available to some users before being generally available. If you want more advanced messaging options, [contact us](/contact_us).

`
// 1. Create a [Discord Webhook](https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks) following these instructions.
// 2. Copy the Webhook URL from Discord
// 3. Go to the [Integrations](/integrations) page and create an Integration with the Discord Webhook Type.
// 4. Paste your URL in to the URL field. Naming your integration is optional.
    useEffect(() => {document.title = 'FreqSignals | Discord'}, [])
    return getPageMarkdownContent(markdown)
}

export function TelegramBotContent() {
    const markdown = `# Telegram Bot
Notify your Telegram group automatically or through curation of signals you're seeing.

You can set up a Telegram Token integration in the [Integrations](/integrations) page and configure your notifications in [Workflows](/workflows).

1. Create a bot
2. Copy the Token
3. Create a Telegram Token integration in the [Integrations](/integrations) page, enter that Token
4. Determine the Chat ID for the Chat room you want the Bot to message (this is configured per-Workflow)

Learn more about [Telegram Bots](https://core.telegram.org/bots)

Advanced messaging is now available to some users before being generally available. If you want more advanced messaging options, [contact us](/contact_us).
`
    useEffect(() => {document.title = 'FreqSignals | Telegram'}, [])
    return getPageMarkdownContent(markdown)
}

export function FreqtradeCryptoBotContent() {
    const markdown = `# Freqtrade Bot

## Overview

Your FreqTrade bot can read data from FreqSignals and inject it into your strategy or send data from FreqTrade to FreqSignals.

See our [example freqtrade strategies](https://github.com/freqsignals/freqtrade-strategies) for working code you can copy.

### Use your own signals
Make FreqSignals your home for your signals. Expensive (money or computation) signals or ones that require external APIs can be stashed in FreqSignals and fetched later.

### Use other people's signals
If someone is providing data you don't easily have, like sentiment data or fundamental data, you can subscribe to their dataset and pull these signals into your trading strategy.

### Sell your signals
Generating some unique indicators? Monetize them by becoming a Data Provider and allowing other people to subscribe to your signals.

## Documentation: Download Signals

Follow the instructions in [the documentation](/documentation) or see our [example freqtrade strategies](https://github.com/freqsignals/freqtrade-strategies) for working code you can copy.

Websocket integration coming soon for easier integration.

### Steps

1. Subscribe to a Data Set.
2. Pull Signals from the API and integrate into your strategy.

## Documentation: Upload Signals

The easiest way to upload your signal from your running FreqTrade bot is by using the Custom message Webhooks, submitted from your strategy.

### Steps

1. Sign up as a Data Provider
2. Create a Data Set. Data Sets are private by default, meaning only your account can see the data you send to them.
3. Create a [Simple API Token Integration](/integrations). Save this, you will use it to send data to your Data Set
4. Configure the webhook within your Freqtrade Configuration File
5. Send the message from your strategy's \`populate_indicators\`. 

Webhook Configuration:

    "webhook": {
        "enabled": true,
        "url": "https://api.freqsignals.com/api/async/freqtrade_webhook/?token=INTERGRATION_TOKEN",
        "retries": 3,
        "retry_delay": 0.2,
        "allow_custom_messages": true,
        "format": "json",
        "strategy_msg": {
            "data": "{msg}"
        }
    }

Strategy \`populate_indicators\`:

    def populate_indicators(self, df: DataFrame, metadata: dict) -> DataFrame:
        # Some logic to establish a signal. For simplicity, using RSI.
        df['rsi'] = ta.RSI(df, timeperiod=14)

        msg = json.dumps({
            # required fields
            "symbol": metadata['pair'],
            "value": round(df.iloc[-1]["rsi"], 4),
            "ttl_minutes": 60,
            "data_set_id": "DATA_SET_ID",
            # any additional context fields
            "rsi": round(df.iloc[-1]["rsi"], 4),
            "price": round(df.iloc[-1]["close"], 4),
            "last_move": round(df.iloc[-1]["close"] - df.iloc[-2]["close"], 4),
        })

        self.dp.send_msg(msg)

        return df

### See Related

- [Freqtrade webhook configuration documentation](https://www.freqtrade.io/en/stable/webhook-config/#configuration)

`
    useEffect(() => {document.title = 'FreqSignals | FreqTrade'}, [])
    return getPageMarkdownContent(markdown)
}

export function TradingViewWebhookContent() {
    const markdown = `# Trading View Webhook Receiver
Receive data from [Trading View Webhooks](https://www.tradingview.com/support/solutions/43000529348-about-webhooks/). Ever want to take your charts in Trading View and distribute them to your community or use your drawings and targets in algorithmic bots? Set up FreqSignal's TradingView Webhook Receiver.

## Steps

1. Sign up as a Data Provider.
2. Create a Data Set. Data Sets are private by default, meaning only your account can see the data you send to them.
3. Create a [Simple API Token Integration](/integrations). Save this, you will use it to send data to your Data Set
4. Set up an Alarm in Trading View.

Webhook URL (replace \`INTERGRATION_TOKEN\`):

    https://api.freqsignals.com/api/aysnc/signals/?token=INTERGRATION_TOKEN

Message (replace \`DATASET_ID\`):

    {
        "symbol": "{{exchange}}:{{ticker}}",
        "value": 0.2,
        "ttl_minutes": 40,
        "data_set_id": "DATASET_ID",
        "price": "{{close}}",
        "volume": "{{volume}}"
    }

Notes:
- The \`value\` is a data-set specific value indicating something. Could be a directional sign, where positive indicates bullishness. Could be an overbought/oversold sign.
- The \`ttl_minutes\` are how long this might be valid for if we don't see another signal overwriting it.
- The \`data_set_id\` is the ID of the dataset. Get this in a page for the Data Set.

It should look something like this:

![Trading View Alert](img/trading_view_alert.png "tradingview alert configuration")

Advanced Example:
Tracking an indicator like Cipher_B_free, which provides "buy" and "sell" signals.

You can configure for when one line crosses above or below the other and relay those numbers to FreqSignals.

![Trading View Alert](img/trading_view_market_cypher_alert.png "tradingview alert market cipher configuration")


    {
        "symbol": "{{exchange}}:{{ticker}}",
        "value": 0.0,
        "ttl_minutes": 60,
        "data_set_id": "DATASET_ID",
        "price": {{close}},
        "volume": {{volume}},
        "wt1": {{plot_5}},
        "wt2": {{plot_6}},
        "cross": {{plot_8}}
    }

Notes:
- \`plot_n\` is a placeholder in TradingView and the 0-19 is from the corresponding output series of the indicator used in the alert. [Learn More](https://www.tradingview.com/chart/?solution=43000531021)
- A signal's value might have no directionality implied, it might just be informational and you want to relay this information or use your bot to optimize how to use this.

`


    useEffect(() => {document.title = 'FreqSignals | Trading View'}, [])
    return getPageMarkdownContent(markdown)
}

export function OnDemandAnalysisContent() {
    const markdown = `# On Demand Analysis
Looking for a one-time deep fundamental or technical analysis? We work with firms to analyze both their data and data that we source.

[Contact us](/contact_us) or connect with our [community](/community). We can evaluate your ask and move forward from there.
`
    useEffect(() => {document.title = 'FreqSignals | On Demand Analysis'}, [])
    return getPageMarkdownContent(markdown)
}
export function AnalysisTrackingContent() {
    const markdown = `# Analysis Tracking
See a stock or crypto asset not followed as closely as you'd like? Want some index developed by us or our partners?

[Contact us](/contact_us) or connect with our [community](/community). We can evaluate your ask and move forward from there.
`
    useEffect(() => {document.title = 'FreqSignals | Analysis Tracking'}, [])
    return getPageMarkdownContent(markdown)
}
export function CustomStrategyContent() {
    const markdown = `# Custom Strategy & Feature Development
We are a team of engineers, quants, economists and finance enthusiasts. If you have a stock or crypto strategy or signal you want automated and existing tooling doesn't seem to handle it for you, we probably have the tools necessary to do it.

We can do custom contract development if the work aligns with our skillset.

[Contact us](/contact_us) or connect with our [community](/community). We can evaluate your ask and move forward from there.
`
    useEffect(() => {document.title = 'FreqSignals | Custom Strategy'}, [])
    return getPageMarkdownContent(markdown)
}

export function AffiliateContent() {
    const markdown = `# Affiliate
## How does it work?
When someone subscribes to FreqSignals PRO using your affiliate link you earn a commission
The commission is 30% of the subscription price on all subsequent subscriptions
Your affiliates must subscribe to FreqSignals PRO within 90 days from the initial visit of the affiliate link.

Payments are made monthly via bank wire transfer, check, PayPal or crypto.

When your balance exceeds $100 for the first time you will be contacted by the FreqSignals Team to arrange your payment.

To get an affiliate link, please [contact us](/contact_us) or connect with our [community](/community).
`
    useEffect(() => {document.title = 'FreqSignals | Affiliate'}, [])
    return getPageMarkdownContent(markdown)
}

export function AdvertisingContent() {
    const markdown = `# Advertising
FreqSignals is used by traders, community leaders, quants, investors and members of financial institutions as the primary platform for online stock market and crypto markets research.

We offer placements for traditional display advertisements, video advertisements and embedded advertisements along with targeting configurations.

If you are interested in advertising with us, please [contact us](/contact_us) or connect with our [community](/community).
`
    useEffect(() => {document.title = 'FreqSignals | Advertising'}, [])
    return getPageMarkdownContent(markdown)
}

export function EnterpriseContent() {
    const markdown = `# Enterprise
We offer Enterprise packages to businesses looking for full control of their own dedicated FreqSignals instance.

This includes, but is not limited to:
- Dedicated Signal Throughput and Volume
- Dedicated Storage
- Security Protocols to match your compliance needs
- Bulk Pricing
- Uptime SLA
- Prioritized Customer Support and Feature Requests
- Much much more! Let us know your needs.


If you are interested in exploring our Enterprise offering, please [contact us](/contact_us).
`
    useEffect(() => {document.title = 'FreqSignals | Enterprise'}, [])
    return getPageMarkdownContent(markdown)
}

export function DataProvidingContent() {
    const markdown = `# Data Providing 1p vs 3p

Providing data to FreqSignals is easy for anyone with an account. You can use your personal datasets for free (with some limitations). If you would like to monetize your data, we offer two routes: 1p and 3p.

## 3p Data Providing as a Seller

Sell your signals or data through FreqSignals, using FreqSignals as a marketplace. Set up your data sources and how you'd like to charge for them. As users sign up for your data sources, you get paid and FreqSignals takes a cut.

## 1p Data Providing as a Vendor

Sell your signals or data to us directly. We come to some payment agreement for your data and you provide it to FreqSignals. We attempt to turn a profit on this data. This usually involved a data trial and evaluation period, for validating the reliability, quality and value of the data. We recommend starting as a 3p data provider.

If you are interested in either, please contact us on our [discord](/community).
`
useEffect(() => {document.title = 'FreqSignals | Data Providing'}, [])
    return getPageMarkdownContent(markdown)
}

export function IntegrationOfferings() {
    const markdown = `# Integrations

Data is at our core and it is our mission to make it easy to send and use data.

![Integrations](img/integrations.png "freqsignal integrations")

### No-Code Solutions

FreqSignals can easily receive real-time data directly from [Trading View](/trading_view_webhook) or data can be uploaded manually in the web-application, no-code required.

FreqSignals can distribute the data directly to [Telegram](/telegram_bot) or [Discord](/discord_bot) or [Email notifications](/email_bot). And the data can always be reviewed in the web-application and sent manually to these destinations.

If you're interested in submitting data from or sending a notification to another platform, let us know!

### Code Solutions

Find our [documentation here](/documentation) for how to submit data using our Token-based OAuth Integration.

Find our [freqsignals-client python library](https://github.com/freqsignals/freqsignals-client) and [freqsignals-client package on pypi](https://pypi.org/project/freqsignals-client/) for easy submission from python-based solutions.

Find [sample strategies](https://github.com/freqsignals/freqsignals-strategies) show how to submit data from [Freqtrade](https://www.freqtrade.io/en/stable/), a popular cryptocurrency trading bot platform.

FreqSignals also supports ingesting data from Amazon Web Services and distributing data to Amazon Web Services. We support the following AWS Services: S3, SQS, SNS, Firehose. Reach out to us to discuss this option, it can not be configured in the UI currently.

### Workflows

FreqSignals manages all of the routing of data from your data subscriptions to your data destinations with [Workflows](/workflows). We can host and route your own private data or route free and paid data sets. Or, you can allow others to subscribe to your data. In some cases, FreqSignals buys data with the intent to use and redistribute, [contact us](/contact_us) if you're interested in providing data.
`
useEffect(() => {document.title = 'FreqSignals | Integration Offerings'}, [])
    return getPageMarkdownContent(markdown)
}

export function TeamContent() {
    const markdown = `# Team

We are a small and nimble team of engineers, quants, economists and finance enthusiasts located in the US and around the world.
`
useEffect(() => {document.title = 'FreqSignals | Team'}, [])
    return getPageMarkdownContent(markdown)
}

export function CareersContent() {
    const markdown = `# Careers

FreqSignals is not actively hiring at this time. If you would like to be involved in some capacity, [contact us](/contact_us).
`
useEffect(() => {document.title = 'FreqSignals | Careers'}, [])
    return getPageMarkdownContent(markdown)
}

export function InvestorsContent() {
    const markdown = `# Investors

FreqSignals is not actively looking for investment.

If you would like to be involved and would consider yourself or your firm to be strategic partners for us, [contact us](/contact_us).
`
    useEffect(() => {document.title = 'FreqSignals | Investors'}, [])
    return getPageMarkdownContent(markdown)
}
export function TermsConditionsContent() {
    const markdown = `# Terms and Conditions
These terms and conditions outline the rules and regulations for the use of FreqSignals's Website, located at https://www.FreqSignals.com.

By accessing this website we assume you accept these terms and conditions. Do not continue to use FreqSignals if you do not agree to take all of the terms and conditions stated on this page.

The following terminology applies to these Terms and Conditions, Privacy Statement and Disclaimer Notice and all Agreements: "Client", "You" and "Your" refers to you, the person log on this website and compliant to the Company’s terms and conditions. "The Company", "Ourselves", "We", "Our" and "Us", refers to our Company. "Party", "Parties", or "Us", refers to both the Client and ourselves. All terms refer to the offer, acceptance and consideration of payment necessary to undertake the process of our assistance to the Client in the most appropriate manner for the express purpose of meeting the Client’s needs in respect of provision of the Company’s stated services, in accordance with and subject to, prevailing law of Netherlands. Any use of the above terminology or other words in the singular, plural, capitalization and/or he/she or they, are taken as interchangeable and therefore as referring to same.
## Disclaimer

There is a high degree of risk involved in trading stocks and options. Past results are not indicative of future returns. FreqSignals is not a registered investment advisor nor is licensed as such with any federal or state regulatory agency. FreqSignals does not manage client assets in any way. Information provided and opinions expressed on the website do not constitute investment advise. Any and all ideas expressed, research, tutorials, and instructional resources are for educational purposes only. The ideas expressed are solely the opinions of FreqSignals . We advise everyone to know the risks involved with trading stocks and options. We encourage every visitor to the website to do his/her own research and to consult with his/her own financial advisor(s) prior to engaging in any investing activities, and to make sure he/she knows all the potential risks involved. Any investment decision that results in losses or gains made based on any information on this site, or related services is not the responsibility of FreqSignals.

Your use of ideas, systems, and/or data provided by FreqSignals are at your own risk and it is your sole responsibility to evaluate the accuracy, completeness, and usefulness of the information.
## Cookies

We employ the use of cookies. By accessing FreqSignals, you agreed to use cookies in agreement with the FreqSignals's Privacy Policy. Most interactive websites use cookies to let us retrieve the user’s details for each visit. Cookies are used by our website to enable the functionality of certain areas to make it easier for people visiting our website. Some of our affiliate/advertising partners may also use cookies.
## License

Unless otherwise stated, FreqSignals and/or its licensors own the intellectual property rights for all material on FreqSignals. All intellectual property rights are reserved. You may access this from FreqSignals for your own personal use subjected to restrictions set in these terms and conditions. You must not: Republish material from FreqSignals Sell, rent or sub-license material from FreqSignals Reproduce, duplicate or copy material from FreqSignals Redistribute content from FreqSignals This Agreement shall begin on the date hereof. Parts of this website offer an opportunity for users to post and exchange opinions and information in certain areas of the website. FreqSignals does not filter, edit, publish or review Comments prior to their presence on the website. Comments do not reflect the views and opinions of FreqSignals,its agents and/or affiliates. Comments reflect the views and opinions of the person who post their views and opinions. To the extent permitted by applicable laws, FreqSignals shall not be liable for the Comments or for any liability, damages or expenses caused and/or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website. FreqSignals reserves the right to monitor all Comments and to remove any Comments which can be considered inappropriate, offensive or causes breach of these Terms and Conditions. You warrant and represent that: You are entitled to post the Comments on our website and have all necessary licenses and consents to do so; The Comments do not invade any intellectual property right, including without limitation copyright, patent or trademark of any third party; The Comments do not contain any defamatory, libelous, offensive, indecent or otherwise unlawful material which is an invasion of privacy The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity. You hereby grant FreqSignals a non-exclusive license to use, reproduce, edit and authorize others to use, reproduce and edit any of your Comments in any and all forms, formats or media.
## iFrames

Without prior approval and written permission, you may not create frames around our Webpages that alter in any way the visual presentation or appearance of our Website.
## Warranties

Neither FreqSignals, its Agencies, Attorneys, Members, Affiliates, nor any Content Partner makes any warranty or representation that the Information is accurate and timely. You understand that use of the Site and of the Products is undertaken at Your own risk. Any information or data provided by FreqSignals is not exhaustive or complete.
## Your Privacy

We respect your privacy rights! We will not sell or distribute your email address or other information without your express consent. We also do not store your any of your credit card information. Only FreqSignals, our Agencies, Attorneys, and Partners are entitled to your information.

## Abuse of Payment Systems

You are entitled to ONE free trial period. Our system automatically checks if you have already signed up for a free trial. If it is found that you are abusing the free trial system, your card will be charged right away and you will be converted to a full subscription instead of availing the trial.


## Duplicate Subscriptions

Please do not click on the subscription button more than once. That can result in multiple subscriptions thereby causing mutliple payments. We will not be held liable in that case. It is the responsibility of the user to make sure they only click on the subscription button once and not get duplicate subscriptions.
## Email Newsletters

When you sign up with an account, you are automatically enrolled in our email newsletter service. If you wish to cancel it, you can either click unsubscribe to the email newsletter in your inbox, or go to your profile and unclick the email checkbox. Newsletters are sent with different frequencies.
## Refund Policy

If you are under a free trial period, you will be charged at the end of your trial unless cancelled. We do not provide refunds. All sales are final. it is your responsibility to manage your subscription accordingly. You may your cancel subscription at any time by clicking on the circle at the top of the website and going to your profile.
## Reservation of Rights

We reserve the right to request that you remove all links or any particular link to our Website. You approve to immediately remove all links to our Website upon request. We also reserve the right to amen these terms and conditions and it’s linking policy at any time. By continuously linking to our Website, you agree to be bound to and follow these linking terms and conditions.
## Removal of links from our website

If you find any link on our Website that is offensive for any reason, you are free to contact and inform us any moment. We will consider requests to remove links but we are not obligated to or so or to respond to you directly. We do not ensure that the information on this website is correct, we do not warrant its completeness or accuracy; nor do we promise to ensure that the website remains available or that the material on the website is kept up to date.
    `
    useEffect(() => {document.title = 'FreqSignals | Terms and Conditions'}, [])
    return getPageMarkdownContent(markdown, fancyStyles)
}

export function DataProviderTermsConditionsContent() {
    const markdown = `# Data Provider Terms and Conditions
   `
    useEffect(() => {document.title = 'FreqSignals | Terms and Conditions'}, [])
    return getPageMarkdownContent(markdown)
}

export function SubscriberTermsConditionsContent() {
    const markdown = `# Subscriber Terms and Conditions
   `
   useEffect(() => {document.title = 'FreqSignals | Terms and Conditions'}, [])
    return getPageMarkdownContent(markdown)
}

export function PrivacyPolicyContent() {
    const markdown = `# PRIVACY POLICY FOR INFORMATION USE AT FREQSIGNALS.COM

FreqSignals.com, ("We") are committed to maintaining the privacy of personal information that you provide to us when using the FreqSignals.com web site. This privacy policy describes how we treat personal information received about you when you visit www.FreqSignals.com.

## Personal Information We Collect

Personally identifiable information (“Personal Information”) is information that can be used to identify or contact you, such as your name, address, email address, telephone number or similar information. We only collect personal information on our web site that you choose to share with us, such as when:

- contacting us via the web site or email
- creating an user account
- creating personal content (presets, portfolios)
- making an online purchase, or subscription to a service
- interacting with our services 

## How We Use Information Collected

We may use information in the following ways:

- For the purposes for which you specifically provided the information.
- To send you e-mail notifications about our new or existing products and services, special offers, or to otherwise contact you.
- To enhance existing features or develop new features, products and services.
- To provide advertisers and other third parties with aggregate information about our user base and usage patterns
- To deliver relevant content and recommendations
- To deliver advertising and interest-based advertising 

Except as specifically stated in this privacy policy, we will not share your personally identifiable information with any third parties outside of FreqSignals.com

## Security

The personally identifiable information is stored in our limited-access servers. We will maintain safeguards to protect the security of these servers and your personally identifiable information. Online purchases made through our web site are processed securely by Stripe.com or PayPal.com. When making a payment, we do not have access to credit card information.


## Links

We may make content or services from other web sites including our co-branded web sites available to you from links located on www.FreqSignals.com. These other web sites are not subject to this privacy policy. We recommend that you review the privacy policy at each such web site to determine how that site protects your privacy.


## Cookies

Like many websites, this website sets and uses cookies to enhance your user experience — to remember your personal settings, for instance. Advertisements may display on this website and, if so, may set and access cookies on your computer; such cookies are subject to the privacy policy of the parties providing the advertisement. However, the parties providing the advertising do not have access to this site's cookies. These parties usually use non-personally-identifiable or anonymous codes to obtain information about your visits to this site.

For more information about this type of online advertising, about cookies, and about how to turn this feature off, please visit this link.

Please note that turning off advertising cookies won’t mean that you are not served any advertising, merely that it will not be tailored to your interests.


## Internet-based Transfers

Given that the internet is a global environment, using the internet to collect and process personal data necessarily involves the transmission of data on an international basis. Therefore, by browsing www.FreqSignals.com and communicating electronically with us you acknowledge and agree to our processing of personal data in this way.


## Advertising

When you use our website, we share information that we collect from you, such as your email (in hashed form), IP address or information about your browser or operating system, with our identity partners/service providers.


## Privacy Notice For California Residents

This PRIVACY NOTICE FOR CALIFORNIA RESIDENTS supplements the information contained in the Privacy Statement of FreqSignals.com (“FreqSignals”, “we,” “us,” or “our”) and applies solely to visitors, users, and others who reside in the State of California (“consumers” or “you”). We adopt this notice to comply with the California Consumer Privacy Act of 2018 (“CCPA”) and other California privacy laws. Please follow the link to access FreqSignals’s California Privacy Notice.


## Comments and Questions

If you have any questions, comments or concerns about our privacy policy, you may email us at support@FreqSignals.com or use contact form.

    `
    useEffect(() => {document.title = 'FreqSignals | Privacy Policy'}, [])
    return getPageMarkdownContent(markdown, fancyStyles)
}


export function CommunityContent() {
    const markdown = `# Community

We are a growing community of traders, quants, engineers, data scientists, economists, investors and finance enthusiasts sharing ideas, charts and signals.

Join our [Discord Server](fs_discord)

Keep conversation civil and respectful. We will not hesitate to ban anyone we feel is creating an unwelcoming environment. Keep it family friendly.

Guidelines:

1. Personal attacks and/or aggressive profanity directed at other members of the community will not be tolerated. Creating unnecessary conflict with other discord members will not be tolerated.
2. Sexually suggestive comments aimed at other members of the community are discouraged.
3. Profanity is allowed, but not when negatively directed at other people in the community.
4. Posting images or links containing sexually explicit material or graphic violence is not allowed.
5. Advertising businesses, discords, NFT's, YouTube channels, courses, communities, random crypto projects, or charities/fundraisers/non-profit organizations is not allowed.
6. Posting referral links or Crypto Addresses is not allowed.
7. Never give out private or personal information. Don't post how much money you have or how much your trade won (or lost).

If you are interested in joining us or are looking to connect with the administrators, join our [Discord Server](fs_discord).

You can also just [contact us](/contact_us) if you're looking to get in touch.

   `
    useEffect(() => {document.title = 'FreqSignals | Community'}, [])
    return getPageMarkdownContent(markdown)
}

export function ContactUsContent() {
    const markdown = `# Contact Us

## Support
For support or issues or feature requests, contact [support@freqsignals.com](copy) or connect with us in our [community on Discord](/community). Please include any steps to reroduce your problem.

## General Communications
For most business communications such as public relations and marketing, partnership and advertising opportunities, or legal matters, please contact [info@freqsignals.com](copy).

## Community
Learn more about joining our [Community](/community) to chat live with us or collaborate more closely.

## Social
Find us on [Twitter @FreqSignals](https://twitter.com/freqsignals), on [Instagram @FreqSignals](https://www.instagram.com/freqsignals/), on [Reddit r/FreqSignals](https://www.reddit.com/r/FreqSignals/)


   `
    useEffect(() => {document.title = 'FreqSignals | Contact Us'}, [])
    return getPageMarkdownContent(markdown)
}

export function DocumentationContent() {
    const markdown = `# Documentation

For support or issues, please contact [support@freqsignals.com](copy) or connect with us on our [community](/community), outlining your issue and steps to reproduce.

## Terminology

### Signal
- Signals are records in a Data Set
- Signals have a \`symbol\` which would usually be an asset's symbol or some index, depending on the nature of the data set.
- Signals have a \`ttl_minutes\` is the time to live, the number of minutes that this signal should be considered valid for. Either the signal expires or the data set should have a new Signal for the symbol before this time, which would update the \`minutes\`. If this is data refreshed daily, 1440 minutes would be a reasonable value.
- Signals have a \`value\` which would indicate the value of this signal. This is very dataset/symbol specific. This might be a price, might be a target price, might be an expected change, might be some indication of overbought or oversold. Or, it might be totally neutral.
- Signals have a \`created_date\` which is the first time we saw this symbol in this dataset.
- Signals have a \`updated_date\` which is the last time this signal was updated.
- Signals are the current record for a symbol within the dataset.
- You see Signals in Data Sets that you are subscribed to.
- Historical values for a Signal are available.

### Data Set
- Data Sets are supplied by Data Providers
- Data Sets can be public, viewable and subscribable for free by any member.
- Data Sets can be private, they can found and subscribed to only by those provided access. They can also be discoverable.
- Data Sets can be published or unpublished. Unpublished Data Sets are entirely private, for the Data Provider to be validating before publishing.

### Integration
- Integrations enable Data Providers to upload Signals to Data Sets.
- Integrations enable Data Set Subscribers to use or download Signals.


## OAuth2 API Token Integration

The OAuth2 API Token provides a "Client ID" and a "Client Secret". These credentials are used to get an Access Token, which can be used for API Requests to fetch and submit signals.

*Note*: The Read Endpoints require this integration, not the Simple Token Integration, which is only used for uploading Signals.
*Note*: If you're using Python, we have a [freqsignals-client python library](https://github.com/freqsignals/freqsignals-client) to assist with this process. 

### 1. Create Tokens

To get a token, login and navigate to the [integrations](/integrations) page. Create an "OAuth2 API Token" integration. Save the Client ID and Client Secret somewhere secure. 

### 2. Get Access Token

Issue an HTTP \`POST\` Request to \`https://api.freqsignals.com/oa2/token/\` with the following body (replacing \`CLIENT_ID\` and \`CLIENT_SECRET\` with your credentials from step 1):

    POST https://api.freqsignals.com/oa2/token/
    Body:
        {
            "grant_type": "client_credentials",
            "client_id": "CLIENT_ID",
            "client_secret": "CLIENT_SECRET"
        }

The response's status code should be \`200\` and the response body should look like:

    {
        "access_token": "abcdefghijklmonpqrstuvwxyz",
        "expires_in": 36000,
        "scope": "read write groups openid",
        "token_type": "Bearer"
    }

The value in the \`access_token\` field is your Access Token.

### 3a. Upload Signals

To submit a signal to FreqSignals, you must register as a Data Provider and set up a Data Set. You can do this when you're logged in on the [Signals page](/signals). You may submit signals through the web-app or programatically from your tool of choice through HTTP Requests.

Issue an HTTP \`POST\` Request to \`https://api.freqsignals.com/api/aysnc/signals/\` with the \`Authorization\` header set to \`Bearer ACCESS_TOKEN\` (replace \`ACCESS_TOKEN\` with your Access Token):

    POST https://api.freqsignals.com/api/aysnc/signals/
    Headers:
        Authorization: Bearer ACCESS_TOKEN
    Body:
        {
            "symbol": "BTC",
            "value": 0.1,
            "ttl_minutes": 60,
            "data_set_id": "DATA_SET_ID"
        }

- \`ACCESS_TOKEN\` is your Access Token from step 2
- \`DATA_SET_ID\` is the Id of your Data Set you're submitting data to 
    

And a body like (replace \`DATA_SET_ID\` with the Id of your Data Set you're submitting data to):

    {
        "symbol": "BTC",
        "value": 0.1,
        "ttl_minutes": 60,
        "data_set_id": "DATA_SET_ID"
    }

You may add attional fields to this, which can add context for a consumer of the data. For example:


    {
        "symbol": "BTC",
        "value": 0.1,
        "ttl_minutes": 60,
        "data_set_id": "DATA_SET_ID",
        "above_bb_upper":0,
        "below_bb_lower":0,
        "rsi_overbought":0,
        "rsi_oversold":0,
        "squeeze_off":1,
        "squeeze_on":0,
    }



### 3b. Download Signals

Issue an HTTP \`GET\` Request to \`https://api.freqsignals.com/api/crud/signals/?limit=10\` with the \`Authorization\` header set to \`Bearer ACCESS_TOKEN\` (replace \`ACCESS_TOKEN\` with your Access Token):

    GET https://api.freqsignals.com/api/crud/signals/?limit=10
    Headers:
        Authorization: Bearer ACCESS_TOKEN

The response should be \`200\` and the body will be a paginated response looking like:


    {
        "count":18,
        "next":"https://api.freqsignals.com/api/crud/signals/?limit=10&offset=10",
        "previous":null,
        "results":[
            {
                "id":"SIGNAL_ID",
                "symbol":"BTC",
                "value": 0.1,
                "ttl_minutes": 60,
                "created_date":"2022-11-03T00:20:04.676982Z",
                "updated_date":"2022-11-03T00:20:04.677004Z",
                "context":{
                    "above_bb_upper":0,
                    "below_bb_lower":0,
                    "rsi_overbought":0,
                    "rsi_oversold":0,
                    "squeeze_off":1,
                    "squeeze_on":0,
                },
                "data_set_id": "DATA_SET_ID"
            }
        ]
    }

Results are ordered by \`updated_date\`, with most recent updates first. Query parameters supported includes:

- \`limit\`: number of results to return, used in pagination
- \`offset\`: number of results to offset by, used in pagination
- \`updated_date__gt\`: filter to results updated more recently than an ISO date
- \`data_set_id\`: filter to results from a specific dataset
- \`symbol\`: filter to results for a specific symbol

Requesting all of a single symbol's signals could look like this

    GET https://api.freqsignals.com/api/crud/signals/?symbol=BTC

Requesting the second page of a data set's signals could look like either of the following:

    GET https://api.freqsignals.com/api/crud/signals/?data_set_id=DATA_SET_ID&offset=10&limit=10
    GET https://api.freqsignals.com/api/crud/signals/?data_set_id=DATA_SET_ID&limit=10&updated_date__gt=2022-11-03T00:20:04.677004Z

If you are running in a loop, it is a best practice to use the \`updated_date__gt\` parameter to filter to just more recent than your most recent signal, stored locally.

3c. Download Historical Signals for a Data Set Symbol

There is a separate endpoint for getting historical values of a Signal. This is because historical data should usually only be fetched once, not each time it is used. So, the response is formatted differently to reduce size in response.
    
    GET /api/crud/signal_history/?symbol=SPY&data_set_id=1234-56-78-9011&limit=1000&updated_date__lt=2022-11-03T00:20:04.677004Z
    Headers:
        Authorization: Bearer ACCESS_TOKEN

The response should be \`200\` and the body will be a paginated response looking like:

        {
            "count":18,
            "next":"/api/crud/signal_history/?symbol=SPY&data_set_id=1234-56-78-9011&limit=1000&updated_date__lt=2022-11-03T00:20:04.676982Z",
            "previous":null,
            "results":[
                {
                    "v": 0.1,
                    "t":"2022-11-03T00:20:04.676982Z",
                    "c":{
                        "above_bb_upper":0,
                        "below_bb_lower":0,
                        "rsi_overbought":0,
                        "rsi_oversold":0,
                        "squeeze_off":1,
                        "squeeze_on":0,
                    },
                }
            ]
        }

*Note*: We use abbreviated values in the signal to reduce the size of the data. We don't include the symbol or the data set, because those were passed in. The mapping is as follows:
- \`v\` is the value
- \`t\` is the created_date - the time from which this value would have been applied
- \`c\` is the context, the values within the context are not abbreviated.

We also support sending Historical Signals in Batch format to Amazon S3. If interested in this, please [contact us](/contact_us).

[Learn more about OAuth2](https://oauth.net/2/grant-types/client-credentials/)


## Simple API Token

The Simple API Token is only available to use to upload Signals. Reading must be done with the Oauth2 API Token Integration (above)

Issue an HTTP \`POST\` Request to \`https://api.freqsignals.com/api/aysnc/signals/?token=TOKEN\` with \`TOKEN\` replaced by your Simple API Token and a body like (replace \`DATA_SET_ID\` with the Id of your Data Set you're submitting data to):

    POST https://api.freqsignals.com/api/aysnc/signals/?token=TOKEN
    Body:
        {
            "symbol": "BTC",
            "value": 0.1,
            "ttl_minutes": 60,
            "data_set_id": DATA_SET_ID,
        }

[Learn more about the Trading View Integration](/trading_view_webhook)

   `
    useEffect(() => {document.title = 'FreqSignals | Contact Us'}, [])
    return getPageMarkdownContent(markdown)
}
